/**
 * Pure grid-rendering geometry for the loadout grid (ADR-0015 §1, §18; ADR-0022).
 *
 * Render-agnostic: turns a `GridPlacement` into CSS-grid span coordinates and
 * answers "which card sits on cell (row, col)". No DOM, no combat state — the
 * combat overlay and the inventory editor both build on these. Grid dimensions
 * are passed in per §18 (player loadout 3×3, enemy loadouts tier-scaled).
 */

import { footprint } from '../grid';
import type { GridPlacement } from '../types';

/** 1-based CSS-grid placement (grid lines start at 1). `rowSpan` / `colSpan`
 * already account for committed rotation via `footprint`. */
export type GridArea = {
  rowStart: number;
  colStart: number;
  rowSpan: number;
  colSpan: number;
};

/** CSS-grid area for a placement. `origin` is 0-based (engine space); CSS grid
 * lines are 1-based, hence the `+ 1`. */
export function cardGridArea(placement: GridPlacement): GridArea {
  const fp = footprint(placement.card, placement.rotated);
  return {
    rowStart: placement.origin.row + 1,
    colStart: placement.origin.col + 1,
    rowSpan: fp.rows,
    colSpan: fp.cols,
  };
}

/** The placement occupying cell (row, col), or null if the cell is empty.
 * Cells are 0-based. Assumes a valid (non-overlapping) grid — returns the first
 * match in iteration order. */
export function cellOccupant(
  placements: readonly GridPlacement[],
  row: number,
  col: number,
): GridPlacement | null {
  for (const placement of placements) {
    const fp = footprint(placement.card, placement.rotated);
    if (
      row >= placement.origin.row &&
      row < placement.origin.row + fp.rows &&
      col >= placement.origin.col &&
      col < placement.origin.col + fp.cols
    ) {
      return placement;
    }
  }
  return null;
}

/** All cell coordinates of a flat `capacity`-sized grid in row-major order — the
 * backing cells the editor and overlay render under the cards (ADR-0015 §18). */
export function allCells(capacity: { rows: number; cols: number }): Array<{
  row: number;
  col: number;
}> {
  const cells: Array<{ row: number; col: number }> = [];
  for (let row = 0; row < capacity.rows; row++) {
    for (let col = 0; col < capacity.cols; col++) {
      cells.push({ row, col });
    }
  }
  return cells;
}
